import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { RouteConfig, ROUTES } from "src/routes/RouteConfig";
import { useAuth } from "src/context/AuthContext";
import { useAuthStore } from "src/store/authStore";
import LoadingSpinner from "src/components/LoadingSpinner";
import { UserType } from "src/context/AuthContext";

// Helper to compute where the user should be redirected
const getRedirectPath = (
  config: RouteConfig,
  userType: UserType | null,
  isOnboarded: boolean | null,
  isAuthenticated: boolean
): string | null => {
  if (!config.requiresAuth) return null;
  if (!isAuthenticated) return ROUTES.LOGIN.path;

  if (config.allowedUserTypes && userType && !config.allowedUserTypes.includes(userType)) {
    return config.fallbackPath || ROUTES.HOME.path;
  }

  if (config.requiresOnboarding && !isOnboarded) {
    if (userType === "student") return ROUTES.STUDENT_ONBOARDING.path;
    if (userType === "tutor") return ROUTES.TUTOR_ONBOARDING.path;
    return config.fallbackPath || ROUTES.HOME.path;
  }

  return null; // User is allowed
};

export const ProtectedRouteWrapper = ({
  children,
  routeConfig,
}: {
  children: React.ReactNode;
  routeConfig: RouteConfig;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { user, userType, isOnboarded, isInitialized, loading } = useAuth();
  const storeUser = useAuthStore((state) => state.user);
  const storeUserType = useAuthStore((state) => state.userType);
  const storeIsOnboarded = useAuthStore((state) => state.isOnboarded);

  const effectiveUser = user || storeUser;
  const effectiveUserType = (userType || storeUserType) as UserType | null;
  const effectiveIsOnboarded =
    isOnboarded !== null ? isOnboarded : storeIsOnboarded;

  useEffect(() => {
    if (!isInitialized || loading) return;

    const isAuthenticated = !!effectiveUser;
    const redirectPath = getRedirectPath(
      routeConfig,
      effectiveUserType,
      effectiveIsOnboarded,
      isAuthenticated
    );

    if (redirectPath && location.pathname !== redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [
    isInitialized,
    loading,
    effectiveUser,
    effectiveUserType,
    effectiveIsOnboarded,
    routeConfig,
    location,
    navigate,
  ]);

  if (!isInitialized || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
        <p className="ml-2">Checking authentication...</p>
      </div>
    );
  }

  return <>{children}</>;
};
